import { inpageWidgetVendorConfigByKey, trackingCpxDomains, trackingCpxWildcardDomainByEnvironmentDefinition } from '@framework/config';
import { createSignal, onMount, createEffect } from 'solid-js';
import { stringSanitizers, cleanCssIdentifier, stripHtmlTags } from '@utils/string-utils';
import { parse } from 'devalue';

// src/client/utils/adfree.util.ts
function checkIsAdFree() {
  try {
    const params = new URLSearchParams(window.location.search);
    return params.get("xr") === "1";
  } catch {
    return false;
  }
}

// src/client/utils/device.util.ts
var BREAKPOINTS = {
  SM: 576,
  MD: 768,
  LG: 996,
  XL: 1200
};
var IS_GOOGLE_BOT_REGEX = /Googlebot|AdsBot-Google|Mediapartners-Google|Storebot-Google|Google-InspectionTool|GoogleOther/i;
var layoutDeviceCategory = () => {
  const viewportWidth = Math.max(document.documentElement.clientWidth || 0, window.innerWidth || 0);
  if (viewportWidth <= BREAKPOINTS.SM) {
    return "mobile";
  } else if (viewportWidth > BREAKPOINTS.MD && viewportWidth < BREAKPOINTS.LG) {
    return "tablet";
  } else {
    return "desktop";
  }
};
var layoutBreakpointCategory = () => {
  const viewportWidth = Math.max(document.documentElement.clientWidth || 0, window.innerWidth || 0);
  if (viewportWidth < BREAKPOINTS.MD) {
    return "SM";
  } else if (viewportWidth >= BREAKPOINTS.MD && viewportWidth < BREAKPOINTS.LG) {
    return "MD";
  } else if (viewportWidth >= BREAKPOINTS.LG && viewportWidth < BREAKPOINTS.XL) {
    return "LG";
  }
  return "XL";
};
var checkIsAppView = () => {
  if (typeof window === "undefined") {
    return false;
  }
  const params = new URLSearchParams(window.location.search);
  return params.has("app");
};
var checkIsGoogleBot = () => {
  const userAgent = window.navigator.userAgent;
  return IS_GOOGLE_BOT_REGEX.test(userAgent);
};

// src/client/utils/consent-layer.util.ts
var Vendors = {
  // Social
  twitter: "5e71760b69966540e4554f01",
  instagram: "5e7e5243b8e05c1c467daa57",
  facebook: "5f1b2fbdb8e05c3057240f56",
  tiktok: "5e7f6927b8e05c4e491e7380",
  youtube: "5e7ac3fae30e7d1bc1ebf5e8",
  tracdelight: "5e77acddd8d48d795087425b",
  // Monetization/CMP Vendors
  outbrain: "5e7ced57b8e05c485246ccde",
  // Sourcepoint custom vendor ID
  cleverpush: "5e77928e9cb08971eb078f60",
  cleverpushIab: "62d1372b293cdf1ca87a36f0",
  taboola: "5e37fc3e56a5e6615502f9c4",
  // Inpage Widget (consent required)
  tennisnet: inpageWidgetVendorConfigByKey("tennisnet").vendorID ?? "",
  finanzen: inpageWidgetVendorConfigByKey("finanzen").vendorID ?? "",
  aroundhome: inpageWidgetVendorConfigByKey("aroundhome").vendorID ?? "",
  tickaroo: inpageWidgetVendorConfigByKey("tickaroo").vendorID ?? "",
  opinary: inpageWidgetVendorConfigByKey("opinary").vendorID ?? "",
  googlemaps: inpageWidgetVendorConfigByKey("googlemaps").vendorID ?? "",
  // further vendors used in tickaroo inpage widget
  apester: "5ef5c3a5b8e05c699f3a0b83",
  glomex: "604b382da2286319d61aa96e",
  pinpoll: "5e7ced57b8e05c485246cce3"
};
var defaultVendorConsents = Object.values(Vendors).reduce((acc, vendorId) => {
  acc[`consent_${vendorId}`] = false;
  return acc;
}, {});
var LOAD_CONSENTS_EVENT_NAME = "getCustomVendorConsents";
var UPDATE_CONSENTS_EVENT_NAME = "postCustomConsent";
var ON_CONSENTS_UPDATED_EVENT_NAME = "CmpConsentUpdate";
var NATIVE_MASTER_CONSENT_MESSAGE_TYPE = "ctf-native-master-vendor-consent";
function buildNativeMasterVendorConsentPayload(vendorId) {
  return {
    channel: NATIVE_MASTER_CONSENT_MESSAGE_TYPE,
    command: UPDATE_CONSENTS_EVENT_NAME,
    version: 2,
    vendorId,
    parameter: [[vendorId], [], []]
  };
}
function pushCustomVendorConsentToNativeParent(vendorId) {
  const payload = buildNativeMasterVendorConsentPayload(vendorId);
  window.flutter_inappwebview?.callHandler?.("cmpHandler", payload);
}
function getVendorConsentsFromWindowOrGetDefaults() {
  if (window.consent) {
    return window.consent;
  }
  return { ...defaultVendorConsents };
}
function calculateUpdatedVendorConsents(vendorConsents, providedGrants) {
  return Object.keys(providedGrants.grants).filter(function ignoreUnknownVendors(vendorId) {
    return Object.values(Vendors).includes(vendorId);
  }).reduce(
    (acc, vendorId) => {
      const isVendorGrantedViaAllConsentsPopup = !!providedGrants.grants[vendorId]?.vendorGrant;
      const isVendorGrantedViaIndividualConsentPopup = !!providedGrants.consentedVendors?.some(({ _id: id }) => id === vendorId);
      const isVendorGrantedViaIndividualConsentButton = !!providedGrants.customVendorsResponse?.consentedVendors?.some(
        ({ _id: id }) => id === vendorId
      );
      const hasVendorConsent = isVendorGrantedViaAllConsentsPopup || isVendorGrantedViaIndividualConsentPopup || isVendorGrantedViaIndividualConsentButton;
      acc[`consent_${vendorId}`] = hasVendorConsent;
      return acc;
    },
    { ...vendorConsents }
  );
}
function ensureVendorConsentsOnWindow() {
  if (!window.consent) {
    window.consent = getVendorConsentsFromWindowOrGetDefaults();
  }
}
function updateVendorConsentsOnWindow(providedGrants) {
  const vendorConsents = getVendorConsentsFromWindowOrGetDefaults();
  const updatedVendorConsents = calculateUpdatedVendorConsents(vendorConsents, providedGrants);
  window.consent = updatedVendorConsents;
  const event = new CustomEvent(ON_CONSENTS_UPDATED_EVENT_NAME, { detail: updatedVendorConsents });
  window.dispatchEvent(event);
}
function onVendorConsentsUpdatedCallback(providedGrants, success) {
  if (success) {
    return updateVendorConsentsOnWindow(providedGrants);
  }
  console.error(`${LOAD_CONSENTS_EVENT_NAME} Failed.`);
}
function checkAndSetConsent() {
  ensureVendorConsentsOnWindow();
  window.__tcfapi(LOAD_CONSENTS_EVENT_NAME, 2, onVendorConsentsUpdatedCallback);
}
function grantConsentForVendor(vendorName) {
  const vendorId = Vendors[vendorName];
  grantConsentForVendorById(vendorId);
}
function grantConsentForVendorById(vendorId) {
  if (!vendorId) {
    return;
  }
  if (checkIsAppView()) {
    pushCustomVendorConsentToNativeParent(vendorId);
  }
  if (typeof window.__tcfapi !== "function") {
    return;
  }
  window.__tcfapi(UPDATE_CONSENTS_EVENT_NAME, 2, onVendorConsentsUpdatedCallback, [vendorId], [], []);
}
function getVendorConsent(vendorId) {
  const vendorConsents = getVendorConsentsFromWindowOrGetDefaults();
  return vendorConsents[`consent_${vendorId}`] ?? false;
}
function registerConsentLayerListener() {
  if (window.__tcfapi) {
    window.__tcfapi("addEventListener", 2, (tcData, success) => {
      if (success) {
        if (tcData.eventStatus === "tcloaded") {
          console.log("tcloaded");
          checkAndSetConsent();
        } else if (tcData.eventStatus === "cmpuishown") {
          console.log("cmpuishown");
        } else if (tcData.eventStatus === "useractioncomplete") {
          console.log("useractioncomplete");
          checkAndSetConsent();
        }
      }
    });
  } else {
    setTimeout(registerConsentLayerListener, 20);
  }
}
function attachConsentLayerListener(callback) {
  window.addEventListener(ON_CONSENTS_UPDATED_EVENT_NAME, callback);
}
function loadWidgetScript(widgetScriptId, widgetSrc, crossOrigin) {
  const scriptElem = document.createElement("script");
  scriptElem.id = widgetScriptId;
  scriptElem.src = widgetSrc;
  if (crossOrigin) {
    scriptElem.crossOrigin = crossOrigin;
  }
  scriptElem.defer = true;
  document.body.appendChild(scriptElem);
}
function getConsentManagerModalOpener(id) {
  return (event) => {
    event.preventDefault();
    window._sp_.gdpr.loadPrivacyManagerModal(id);
  };
}
function getUtiqManageModalOpener() {
  return (event) => {
    event.preventDefault();
    const utiq = window.Utiq;
    if (utiq?.queue) {
      utiq.queue.push(() => utiq.API.showConsentModal());
    }
  };
}
function getConsentRevealClickHandler() {
  let clickCount = 0;
  let clickTimeout = null;
  const reset = () => {
    clickCount = 0;
    if (clickTimeout) {
      clearTimeout(clickTimeout);
      clickTimeout = null;
    }
  };
  const handleClick = () => {
    clickCount += 1;
    if (clickCount === 1) {
      clickTimeout = setTimeout(reset, 1e4);
    }
    if (clickCount === 10) {
      reset();
      window.__tcfapi("getTCData", 2, (tcData) => navigator.clipboard.writeText(tcData.tcString));
    }
  };
  return handleClick;
}
function hasLegacyPurposeBasedWidgetConsent(tcData) {
  return Object.values(tcData.purpose?.consents ?? {}).some((granted) => granted === true);
}
var waitForConsentLayerLoaded = () => {
  return new Promise((resolve) => {
    const intervalId = setInterval(() => {
      if (window.__tcfapi) {
        window.__tcfapi("addEventListener", 2, (tcData, success) => {
          if (success && ["tcloaded", "useractioncomplete"].includes(tcData.eventStatus)) {
            resolve();
            clearInterval(intervalId);
          }
        });
      }
    }, 20);
  });
};
function useWidgetConsentState(vendorId, showWidget) {
  const [isLoadingConsents, setIsLoadingConsents] = createSignal(true);
  const [hasConsent, setHasConsent] = createSignal(false);
  onMount(function initializeWidgetWhenConsentLoaded() {
    waitForConsentLayerLoaded().then(() => {
      checkVendorConsentAndUpdateLocalState();
      attachConsentLayerListener(checkVendorConsentAndUpdateLocalState);
      setIsLoadingConsents(false);
      showWidgetIfConsentExists();
    }).catch(() => {
    });
  });
  createEffect(showWidgetIfConsentExists);
  function showWidgetIfConsentExists() {
    if (hasConsent()) {
      showWidget();
    }
  }
  function checkVendorConsentAndUpdateLocalState() {
    const vendorIds = typeof vendorId === "string" ? [vendorId] : vendorId;
    setHasConsent(vendorIds.some((id) => getVendorConsent(id)));
  }
  return {
    isLoadingConsents,
    hasConsent
  };
}
function usePurposeBasedWidgetConsentState(showWidget) {
  const [isLoadingConsents, setIsLoadingConsents] = createSignal(true);
  const [hasConsent, setHasConsent] = createSignal(false);
  onMount(function initializeWidgetWhenPurposeConsentLoaded() {
    const updateFromTcData = (tcData, success) => {
      if (!success || !["tcloaded", "useractioncomplete"].includes(tcData.eventStatus)) {
        return;
      }
      setHasConsent(hasLegacyPurposeBasedWidgetConsent(tcData));
      setIsLoadingConsents(false);
    };
    waitForConsentLayerLoaded().then(() => {
      window.__tcfapi("addEventListener", 2, updateFromTcData);
      window.__tcfapi("getTCData", 2, updateFromTcData);
    }).catch(() => {
    });
  });
  createEffect(showWidgetIfConsentExists);
  function showWidgetIfConsentExists() {
    if (hasConsent()) {
      showWidget();
    }
  }
  return {
    isLoadingConsents,
    hasConsent
  };
}
function waitForDocumentReady() {
  return new Promise((resolve) => {
    if (document.readyState === "interactive" || document.readyState === "complete") {
      resolve();
    } else {
      const controller = new AbortController();
      document.addEventListener(
        "readystatechange",
        () => {
          if (document.readyState === "interactive" || document.readyState === "complete") {
            controller.abort();
            resolve();
          }
        },
        { signal: controller.signal }
      );
    }
  });
}
function retryCallbackWithCondition(callback, condition, interval = 500, maxRetries = 10) {
  if (condition()) {
    callback();
    return;
  }
  let retries = 0;
  const intervalId = setInterval(() => {
    if (condition()) {
      clearInterval(intervalId);
      return callback();
    } else if (retries >= maxRetries) {
      clearInterval(intervalId);
      console.error("Condition not met after maximum retries");
    }
    retries++;
  }, interval);
}
function isUtagDefinedOnWindow() {
  return typeof window.utag !== "undefined";
}
function isUtagLinkDefinedOnWindow() {
  return isUtagDefinedOnWindow() && typeof window.utag.link === "function";
}
function isCpxLink(href) {
  const hrefUrl = new URL(href);
  const hostname = hrefUrl.hostname.replace(/(www\.)?/, "");
  if (trackingCpxDomains.includes(hostname)) {
    return true;
  }
  const { wildcardDomain } = trackingCpxWildcardDomainByEnvironmentDefinition();
  return hostname === wildcardDomain.slice(1) || hostname.endsWith(wildcardDomain);
}
function isInternalLink(linkUrl, pageUrl) {
  const normalizedLinkUrl = linkUrl.hostname.replace(/^www\./, "");
  const normalizedPageUrl = pageUrl.hostname.replace(/^www\./, "");
  return normalizedLinkUrl === normalizedPageUrl || normalizedLinkUrl.endsWith("." + normalizedPageUrl);
}

// src/utils/abHeadlineDomUuid.ts
var AB_HEADLINE_DOM_UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
function normalizeAbHeadlineUuidForTracking(raw) {
  const s = raw.trim();
  if (!s || !AB_HEADLINE_DOM_UUID_RE.test(s)) {
    return void 0;
  }
  return s.toLowerCase();
}
var sanitizeString = (text) => {
  if (!text) {
    return "";
  }
  return stringSanitizers.normalizeElementTrackingString(text);
};
var ElementTrackingAdapter = ({
  name,
  blockIndex,
  context,
  nestedContext,
  channel,
  entityId,
  label,
  property,
  value,
  position,
  pinPosition,
  stageTitle,
  stageLayout,
  excludeLinksFromViewEvents,
  stageArticleSourceType,
  campaignId,
  campaignName,
  creative,
  customViewTrackingUrl,
  layout,
  load = false,
  view = true,
  click = true,
  swipe = false
}) => {
  const result = {
    "data-element-tracking-name": name
  };
  if (typeof blockIndex === "number") {
    result["data-element-tracking-block-index"] = (blockIndex + 1).toString();
  }
  if (channel) {
    result["data-element-tracking-channel"] = sanitizeString(channel);
  }
  if (context) {
    result["data-element-tracking-context"] = context;
  }
  if (nestedContext) {
    result["data-element-tracking-nested-context"] = sanitizeString(nestedContext);
  }
  if (entityId) {
    result["data-element-tracking-id"] = sanitizeString(entityId);
  }
  if (stageArticleSourceType) {
    result["data-element-tracking-stage-article-source-type"] = sanitizeString(stageArticleSourceType).replace(/_/g, "-");
  }
  if (position) {
    result["data-element-tracking-position"] = sanitizeString(position);
  }
  if (pinPosition) {
    result["data-element-tracking-pin-position"] = pinPosition;
  }
  if (stageTitle) {
    result["data-element-tracking-stage-title"] = sanitizeString(stageTitle);
  }
  if (stageLayout) {
    result["data-element-tracking-stage-layout"] = sanitizeString(stageLayout);
  }
  if (excludeLinksFromViewEvents) {
    result["data-element-tracking-exclude-links-from-view-events"] = true;
  }
  if (label) {
    result["data-element-tracking-label"] = label;
  }
  if (property) {
    result["data-element-tracking-property"] = stringSanitizers.removeHTMLTagsAndQuotationMarks(property);
  }
  if (value) {
    result["data-element-tracking-value"] = value;
  }
  if (campaignId) {
    result["data-element-tracking-campaign-id"] = stringSanitizers.removeHTMLTagsAndQuotationMarks(campaignId);
  }
  if (campaignName) {
    result["data-element-tracking-campaign-name"] = stringSanitizers.removeHTMLTagsAndQuotationMarks(campaignName);
  }
  if (creative) {
    result["data-element-tracking-creative"] = sanitizeString(creative).replace(/_/g, "-");
  }
  if (customViewTrackingUrl) {
    result["data-element-tracking-custom-view-url"] = stringSanitizers.removeHTMLTagsAndQuotationMarks(customViewTrackingUrl);
  }
  if (layout) {
    result["data-element-tracking-layout"] = layout;
  }
  if (!load) {
    result["data-element-tracking-load"] = false;
  }
  if (!view) {
    result["data-element-tracking-view"] = false;
  }
  if (!click) {
    result["data-element-tracking-click"] = false;
  }
  if (!swipe) {
    result["data-element-tracking-swipe"] = false;
  }
  return Object.freeze(result);
};

// src/client/adapters/element-tracking/ChannelElementTracking.adapter.ts
var channelPagesTrackingExclusions = [
  "article-teaser",
  "newsticker",
  "breaking_news",
  "article-link-debate",
  "gallery-teaser",
  "button-channel-tag",
  "button-more"
];
var ChannelElementTrackingAdapter = (args) => {
  const { isHomePage = false, ...elementTrackingArgs } = args;
  const shouldExcludeTracking = !isHomePage && channelPagesTrackingExclusions.includes(args.name);
  if (shouldExcludeTracking) {
    return void 0;
  }
  return ElementTrackingAdapter(elementTrackingArgs);
};

// src/client/adapters/element-tracking/buildArticleTeaserTrackingCampaignName.ts
var buildArticleTeaserTrackingCampaignName = (item) => {
  const articleId = item._id ?? item.tracking?.trackingId;
  const label = item.initialPublishedTitle ?? item.title;
  return `${articleId ? `ctf-${articleId}` : "n/a"} | ${label}`;
};

// src/client/utils/elementTrackingAPI.ts
var ElementTrackingAPI = class _ElementTrackingAPI {
  static _instance = null;
  _pageLoadTimer = 0;
  _initialized = false;
  _linkUuidPattern = /.*\/.+_([a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12})\.html$/;
  /**
   * A map that stores the positions of tracked elements in regards to their context and position within page.
   * The key is an HTMLElement and the value is an IElementPosition object.
   */
  _elementPositions = /* @__PURE__ */ new Map();
  /**
   * A map that holds information about hyperlinks within tracked elements.
   * The key is an HTMLElement and the value is an array of IClickableElementData objects.
   * This map is used to track clicks and extract relevant data such as the link's UUID, text content, URL, and URL type.
   */
  _elementClickableItems = /* @__PURE__ */ new Map();
  _EventCategory = "pageElement";
  constructor() {
  }
  static getInstance() {
    if (typeof window === "undefined" || typeof document === "undefined" || typeof IntersectionObserver === "undefined") {
      return new DummyElementTrackingAPI();
    }
    if (!_ElementTrackingAPI._instance) {
      _ElementTrackingAPI._instance = new _ElementTrackingAPI();
    }
    return _ElementTrackingAPI._instance;
  }
  /**
   * Initializes the element tracking system if it has not been initialized yet.
   * If the tracking system is already initialized, a warning is logged to the console.
   *
   * This method uses a retry mechanism to wait until `window.utag` is available,
   * then proceeds to initialize tracking by calling `_initializeTracking`.
   *
   * @returns {void}
   */
  bootstrapTracking() {
    if (this._initialized) {
      console.warn("ElementTrackingAPI: Already initialized.");
      return;
    }
    retryCallbackWithCondition(() => this._initializeTracking(), isUtagLinkDefinedOnWindow, 50, 100);
  }
  /**
   * Initializes the tracking of eligible elements on the page.
   *
   * This method sets the `_initialized` flag to true and retrieves all trackable elements.
   * If no trackable elements are found, the method returns early.
   *
   * The method performs the following actions on the trackable elements:
   * - Extracts clickable element data.
   * - Calculates element positions within the context.
   * - Tracks the page load.
   * - Sets up view tracking for elements with `data-element-tracking-view` attribute not set to 'false'.
   * - Sets up click tracking for elements with `data-element-tracking-click` attribute not set to 'false'.
   * - Sets up swipe tracking for elements with `data-element-tracking-swipe` attribute not set to 'false'.
   *
   * Finally, it logs a message to the console indicating that tracking has been successfully initialized.
   *
   * @private
   * @returns {void}
   */
  _initializeTracking() {
    this._initialized = true;
    this._pageLoadTimer = performance.now();
    const trackableElements = this._getTrackableElements();
    if (trackableElements.length === 0) {
      return;
    }
    this._extractClickableElementData(trackableElements);
    this._calculateElementPositionsWithinContext(trackableElements);
    this._setupViewTracking(trackableElements.filter((el) => el.getAttribute("data-element-tracking-view") !== "false"));
    this._setupClickTracking(this._getClickTrackableElements(trackableElements));
    this._setupSwipeTracking(trackableElements.filter((el) => el.getAttribute("data-element-tracking-swipe") !== "false"));
  }
  _getClickTrackableElements(elements) {
    return elements.filter((el) => el.getAttribute("data-element-tracking-click") !== "false" || this._isSnapSliderControlElement(el));
  }
  _isSnapSliderControlElement(element) {
    const pageElementName = element.getAttribute("data-element-tracking-name");
    return element.tagName === "BUTTON" && (pageElementName === "arrow-button" || pageElementName === "arrow-button-pagination" || pageElementName === "pagination-dot");
  }
  _getTrackableElements() {
    return Array.from(document.querySelectorAll("[data-element-tracking-name]"));
  }
  /**
   * Calculates the positions of elements within their respective contexts and updates the element positions map.
   *
   * @param elements - An array of HTML elements to be processed.
   *
   * This method performs the following steps:
   * 1. Filters out elements whose positions should be ignored.
   * 2. Groups the remaining elements by their context.
   * 3. Iterates over each context group and assigns position-related attributes to each element.
   *
   * For each element, the following attributes are calculated and stored:
   * - `isStandalone`: Indicates whether the element is standalone or part of a nested context group.
   * - `blockPosition`: The block index of the element, derived from the `data-element-tracking-block-index` attribute.
   * - `contextPosition`: The position of the element within its context group.
   * - `absolutePagePosition`: The position of the element within the entire list of tracked elements.
   * - `listItemPosition` (for nested elements): The position of the element within its nested context group.
   * - `listItemsCount` (for nested elements): The total number of elements within the nested context group.
   */
  _calculateElementPositionsWithinContext(elements) {
    const trackedElementsWithPositionEnabled = elements.filter((element) => !this._isElementPositionIgnored(element));
    const trackedElementsGroupedByContext = this._groupElementsByContext(trackedElementsWithPositionEnabled);
    trackedElementsGroupedByContext.forEach((contextGroup) => {
      let contextPosition = 0;
      contextGroup.forEach((item) => {
        if (Array.isArray(item)) {
          item.forEach((nestedElement, nestedIndex) => {
            const blockIndex = nestedElement.getAttribute("data-element-tracking-block-index");
            const blockIndexAsNumber = blockIndex ? Number(blockIndex) : 0;
            const isPositionPinned = this._isElementPositionPinned(nestedElement);
            this._elementPositions.set(nestedElement, {
              isStandalone: false,
              blockPosition: blockIndexAsNumber,
              contextPosition: isPositionPinned ? contextPosition : ++contextPosition,
              absolutePagePosition: trackedElementsWithPositionEnabled.indexOf(nestedElement) + 1,
              listItemPosition: nestedIndex + 1,
              listItemsCount: item.length
            });
          });
        } else {
          const blockIndex = item.getAttribute("data-element-tracking-block-index");
          const blockIndexAsNumber = blockIndex ? Number(blockIndex) : 0;
          const isPositionPinned = this._isElementPositionPinned(item);
          this._elementPositions.set(item, {
            isStandalone: true,
            blockPosition: blockIndexAsNumber,
            contextPosition: isPositionPinned ? contextPosition : ++contextPosition,
            absolutePagePosition: trackedElementsWithPositionEnabled.indexOf(item) + 1
          });
        }
      });
    });
  }
  _extractClickableElementDataAttributeValue(link, linkDataAttribute = "data-element-tracking-custom-value") {
    return sanitizeString(link.getAttribute(linkDataAttribute) ?? "") ?? "";
  }
  /**
   * Extracts clickable element data from a list of HTML elements and stores the data in a map.
   *
   * @param elements - An array of HTML elements to extract clickable data from.
   *
   * This method iterates over each element in the provided array, finds all hyperlinks within each element,
   * and extracts relevant data such as the link's UUID, text content, URL, and URL type (internal or external).
   * The extracted data is then stored in a map where the key is the element and the value is an array of clickable element data.
   */
  _extractClickableElementData(elements) {
    elements.forEach((element) => {
      const elementClickableItems = [];
      const attrOrUndefined = this._createGetAttributeOrUndefined(element);
      const pageElementName = attrOrUndefined("data-element-tracking-name");
      if (this._isSnapSliderControlElement(element)) {
        elementClickableItems.push({
          element,
          label: "n/a",
          urlType: "pageElement.clickInternal"
        });
      } else if (pageElementName && pageElementName === "newsletter-widget") {
        const newsletterButton = element.querySelector("button[data-element-tracking-newsletter-button]");
        if (newsletterButton) {
          const buttonText = newsletterButton.textContent?.trim() || "";
          const urlType = "pageElement.clickInternal";
          elementClickableItems.push({
            element: newsletterButton,
            label: "",
            // Make empty in order to use customLabel with button text without auto calculation of pageElementCampaignName
            customLabel: sanitizeString(buttonText),
            urlType
          });
        }
      } else {
        const hyperlinksWithinTrackedElement = element.querySelectorAll("a");
        hyperlinksWithinTrackedElement.forEach((link, index) => {
          const linkUuid = this._linkUuidPattern.exec(link.getAttribute("href") || "")?.[1];
          const linkText = link.textContent?.trim() || "";
          const linkUrl = new URL(link.getAttribute("href") || "", document.baseURI);
          const pageUrl = new URL(window.location.href);
          const urlType = isInternalLink(linkUrl, pageUrl) ? "pageElement.clickInternal" : "pageElement.clickExternal";
          const totalLinks = hyperlinksWithinTrackedElement.length;
          const linkIndex = Number(index + 1);
          elementClickableItems.push({
            element: link,
            label: linkText,
            url: linkUrl.toString(),
            urlType,
            ctfUuid: linkUuid,
            index: linkIndex,
            total: totalLinks
          });
        });
      }
      this._elementClickableItems.set(element, elementClickableItems);
    });
  }
  /**
   * Tracks the loading of page elements by collecting their attributes and dispatching an event.
   *
   * @private
   * @param {HTMLElement[]} elements - An array of HTML elements to be tracked.
   *
   * This method performs the following steps:
   * 1. Maps over the provided elements to collect their tracking attributes and other relevant data.
   * 2. Sets the `_pageLoadTimer` to the current performance time.
   * 3. Constructs an `IElementTrackingEvent` object with the collected data.
   * 4. Dispatches the constructed event using `_dispatchElementTrackingEvent`.
   */
  _trackPageLoad(elements) {
    if (elements && elements.length > 0) {
      const pageElementData = elements.map((element) => {
        const attrOrUndefined = this._createGetAttributeOrUndefined(element);
        return {
          pageElementId: attrOrUndefined("data-element-tracking-id"),
          pageElementName: attrOrUndefined("data-element-tracking-name"),
          pageElementType: this._getPageElementType(element),
          pageElementPosition: this._getPageElementPosition(element)
        };
      });
      const event = {
        event_name: "pageElement.load",
        event_action: "pageElement.load",
        event_category: this._EventCategory,
        event_label: "n/a",
        pageElementData
      };
      this._dispatchElementTrackingEvent(event);
    }
  }
  _prepareCustomTrackingImg(url) {
    const trackingImg = document.createElement("img");
    trackingImg.src = url;
    trackingImg.width = 0;
    trackingImg.height = 0;
    trackingImg.style.display = "none";
    return trackingImg;
  }
  /**
   * Sets up view tracking for the given elements using the IntersectionObserver API.
   *
   * This method observes the provided elements and triggers a tracking event when
   * an element becomes visible in the viewport. The tracking event is dispatched
   * only once per element.
   *
   * @param elements - An array of HTML elements to be tracked for visibility.
   *
   * @remarks
   * The IntersectionObserver is configured with a threshold of 0.5, meaning the
   * callback is executed when at least 50% of the element is visible in the viewport.
   *
   * The `_prepareTrackingData` method is used to prepare the tracking data for each element.
   * The `_dispatchElementTrackingEvent` method is used to dispatch the tracking event.
   */
  _setupViewTracking(elements) {
    const observer = new IntersectionObserver(
      (entries, observer2) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const element = entry.target;
            const trackingDataArray = this._prepareTrackingData({ element, eventName: "pageElement.view" });
            trackingDataArray.forEach((trackingData) => {
              const event = {
                ...trackingData,
                ...this._getAbHeadlineFieldsForEvent(element, "pageElement.view"),
                event_name: "pageElement.view",
                event_action: "pageElement.view",
                event_category: this._EventCategory
              };
              this._dispatchElementTrackingEvent(event);
            });
            if (element.hasAttribute("data-element-tracking-custom-view-url")) {
              const trackingUrl = element.getAttribute("data-element-tracking-custom-view-url") || "";
              element.appendChild(this._prepareCustomTrackingImg(trackingUrl));
            }
            observer2.unobserve(element);
          }
        });
      },
      { threshold: 0.5 }
    );
    elements.forEach((el) => observer.observe(el));
  }
  /**
   * Sets up click tracking for the provided elements.
   *
   * This method attaches click event listeners to all hyperlinks found within the tracked elements.
   * Instead of tracking clicks on the elements themselves, it tracks clicks on the hyperlinks within each element.
   *
   * @param elements - An array of HTML elements to set up click tracking for.
   */
  _setupClickTracking(elements) {
    elements.forEach((element) => {
      this._elementClickableItems.get(element)?.forEach((clickableItemData) => {
        clickableItemData.element.addEventListener("click", (event) => {
          const eventAction = this._getEventActionForClickEvent(clickableItemData);
          const eventValue = this._getEventValue();
          const trackingDataArray = this._prepareTrackingData({ element, eventName: "pageElement.click", clickableItemData });
          trackingDataArray.forEach((trackingData) => {
            const trackingEvent = {
              ...trackingData,
              ...this._getAbHeadlineFieldsForEvent(element, eventAction),
              event_name: "pageElement.click",
              event_action: eventAction,
              event_value: eventValue
            };
            if (trackingData.pageElementName === "cta-button") {
              if (clickableItemData.url && isCpxLink(clickableItemData.url)) {
                return;
              }
              trackingEvent.event_value = void 0;
              trackingEvent.pageElementType = void 0;
              trackingEvent.pageElementCampaignId = void 0;
            }
            if (trackingData.pageElementName === "related-article-linkbox") {
              trackingEvent.event_value = void 0;
              trackingEvent.pageElementType = void 0;
              trackingEvent.event_property = void 0;
              trackingEvent.pageElementPosition = `${trackingEvent.pageElementPosition} | ${clickableItemData.index}-${clickableItemData.total}`;
            }
            if (trackingData.pageElementName === "background-article-linkbox") {
              trackingEvent.event_value = void 0;
            }
            if (trackingData.pageElementName === "partner-linkbox") {
              trackingEvent.event_value = void 0;
              trackingEvent.event_property = void 0;
            }
            if (trackingData.pageElementName === "newsletter-widget") {
              trackingEvent.event_value = void 0;
              trackingEvent.event_label = "newsletter-registration";
            }
            if (trackingData.pageElementName === "arrow-button" || trackingData.pageElementName === "arrow-button-pagination" || trackingData.pageElementName === "pagination-dot") {
              const blockIndex = element.getAttribute("data-element-tracking-block-index") || "0";
              const stageTitle = element.getAttribute("data-element-tracking-stage-title") || "no-stage-title";
              const stageLayout = element.getAttribute("data-element-tracking-stage-layout") || "n/a";
              trackingEvent.pageElementType = `${blockIndex} | ${stageTitle} | ${stageLayout}`;
              trackingEvent.event_property = void 0;
              if (trackingData.pageElementName === "pagination-dot") {
                trackingEvent.pageElementCampaignName = void 0;
              }
            }
            if (trackingData.pageElementType?.includes("reporting_box")) {
              const trackableChild = event.target;
              trackingEvent.event_property = trackableChild.getAttribute("data-custom-element-tracking-property") || trackingEvent.event_property;
            }
            this._dispatchElementTrackingEvent(trackingEvent);
          });
        });
      });
    });
  }
  _setupSwipeTracking(elements) {
    const SWIPE_THRESHOLD_PX = 25;
    elements.forEach((element) => {
      element.addEventListener("pointerdown", (event) => {
        if (event.pointerType === "mouse" && event.button !== 0) {
          return;
        }
        const pointerStartX = event.clientX;
        const pointerId = event.pointerId;
        document.addEventListener(
          "pointerup",
          (upEvent) => {
            if (upEvent.pointerId !== pointerId) {
              return;
            }
            const deltaX = upEvent.clientX - pointerStartX;
            if (Math.abs(deltaX) <= SWIPE_THRESHOLD_PX) {
              return;
            }
            const trackingDataArray = this._prepareTrackingData({ element, eventName: "pageElement.swipe" });
            trackingDataArray.forEach((trackingData) => {
              const trackingEvent = {
                ...trackingData,
                event_name: "pageElement.swipe",
                event_action: "pageElement.swipe",
                event_category: this._EventCategory
              };
              this._dispatchElementTrackingEvent(trackingEvent);
            });
          },
          { once: true }
        );
        document.addEventListener(
          "pointercancel",
          (cancelEvent) => {
            if (cancelEvent.pointerId !== pointerId) {
              return;
            }
          },
          { once: true }
        );
      });
    });
  }
  /**
   * Prepares tracking data for the given tracked event.
   *
   * @param trackedEventInfo - The information about the tracked event.
   * @returns An array of tracking data objects.
   *
   * This method extracts various attributes from the provided element and constructs
   * a base tracking data object. Depending on the event type and the presence of
   * clickable items within the element, it augments the base tracking data with
   * additional information such as event labels and campaign names.
   
   * The method handles three main scenarios:
   * 1. If the event is a click event and the clicked element is a hyperlink, it returns
   *    tracking data for the clicked hyperlink.
   * 2. If the element contains multiple hyperlinks, it returns tracking data for each
   *    clickable item within the element.
   * 3. If the element does not contain hyperlinks, it returns a single tracking data
   *    object with a default event label.
   */
  _prepareTrackingData(trackedEventInfo) {
    const { element, eventName, clickableItemData } = trackedEventInfo;
    const attrOrUndefined = this._createGetAttributeOrUndefined(element);
    const containsHyperlinks = !!this._elementClickableItems.get(element)?.length;
    const shouldExcludeLinksFromViewEvents = attrOrUndefined("data-element-tracking-exclude-links-from-view-events") === "true";
    const baseTrackingData = {
      event_property: attrOrUndefined("data-element-tracking-property"),
      event_value: this._getEventValue(),
      event_category: this._EventCategory,
      pageElementId: attrOrUndefined("data-element-tracking-id"),
      pageElementName: attrOrUndefined("data-element-tracking-name"),
      pageElementType: this._getPageElementType(element),
      pageElementPosition: this._getPageElementPosition(element),
      pageElementCreative: this._getPageElementCreative(element),
      pageElementCampaignId: attrOrUndefined("data-element-tracking-campaign-id")
    };
    if (clickableItemData) {
      const trackingData2 = {
        ...baseTrackingData,
        event_label: this._getEventLabel(eventName, clickableItemData),
        pageElementCampaignName: this._getPageElementCampaignName(trackedEventInfo, clickableItemData)
      };
      if (baseTrackingData.pageElementName === "newsticker") {
        const articleType = this._extractClickableElementDataAttributeValue(
          clickableItemData.element,
          "data-element-tracking-article-source-type"
        );
        trackingData2.pageElementCreative = `${clickableItemData.index}-${clickableItemData.total}${articleType ? " | " + articleType : ""}`;
      }
      return [trackingData2];
    }
    if (containsHyperlinks && !shouldExcludeLinksFromViewEvents) {
      return this._elementClickableItems.get(element)?.map((clickableElementData) => {
        const trackingData2 = {
          ...baseTrackingData,
          event_label: this._getEventLabel(eventName, clickableElementData),
          pageElementCampaignName: this._getPageElementCampaignName(trackedEventInfo, clickableElementData)
        };
        if (baseTrackingData.pageElementName === "newsticker") {
          const articleType = this._extractClickableElementDataAttributeValue(
            clickableElementData.element,
            "data-element-tracking-article-source-type"
          );
          trackingData2.pageElementCreative = `${clickableElementData.index}-${clickableElementData.total}${articleType ? " | " + articleType : ""}`;
        }
        return trackingData2;
      }) ?? [];
    }
    const trackingData = {
      ...baseTrackingData,
      event_label: "n/a",
      pageElementCampaignName: "n/a"
    };
    return [trackingData];
  }
  _getEventLabel(eventName, clickableElementData) {
    return eventName === "pageElement.load" ? "n/a" : clickableElementData.url || "n/a";
  }
  _getEventValue() {
    const pageValueFromAttribute = document.body.getAttribute("data-element-tracking-value");
    if (pageValueFromAttribute) {
      return pageValueFromAttribute;
    }
    return ((performance.now() - this._pageLoadTimer) / 1e3).toFixed(1);
  }
  _getEventActionForClickEvent(clickableItemData) {
    return clickableItemData?.urlType || "pageElement.clickInternal";
  }
  /**
   * Snowplow A/B headline fields from the teaser root. Only for `pageElement.view` and
   * `pageElement.clickInternal`. Returns nothing unless `data-ab-teaser`, `data-ab-variant-id`,
   * and `data-ab-variant-title` are all set (channel runner fills UUIDs + title after pick).
   * `data-ab-teaser` and `data-ab-variant-id` are hyphenated Mongo UUIDs; Snowplow gets lowercase
   * canonical ids via `normalizeAbHeadlineUuidForTracking`.
   */
  _getAbHeadlineFieldsForEvent(element, eventAction) {
    if (eventAction !== "pageElement.view" && eventAction !== "pageElement.clickInternal") {
      return void 0;
    }
    const root = element.closest("[data-ab-teaser]");
    if (!root) {
      return void 0;
    }
    const testRaw = root.getAttribute("data-ab-teaser") || "";
    const variantRaw = root.getAttribute("data-ab-variant-id") || "";
    const variantTitle = root.getAttribute("data-ab-variant-title") || "";
    if (!testRaw || !variantRaw || !variantTitle) {
      return void 0;
    }
    const abHeadlineTestId = normalizeAbHeadlineUuidForTracking(testRaw);
    const abHeadlineTestVariantId = normalizeAbHeadlineUuidForTracking(variantRaw);
    if (!abHeadlineTestId || !abHeadlineTestVariantId) {
      return void 0;
    }
    return {
      abHeadlineTestId,
      abHeadlineTestVariantId,
      abHeadlineTestVariant: variantTitle
    };
  }
  _getPageElementType(element) {
    const isElementPositionIgnored = this._isElementPositionIgnored(element);
    if (isElementPositionIgnored) {
      return;
    }
    const blockPosition = this._elementPositions.get(element)?.blockPosition || 0;
    const stageTitle = element.getAttribute("data-element-tracking-stage-title") || "no-stage-title";
    const stageLayout = element.getAttribute("data-element-tracking-stage-layout") || "n/a";
    return `${blockPosition} | ${stageTitle} | ${stageLayout}`;
  }
  _getPageElementPosition(element) {
    const positionFromAttribute = element.getAttribute("data-element-tracking-position");
    const isElementPositionIgnored = this._isElementPositionIgnored(element);
    const channelName = element.getAttribute("data-element-tracking-channel");
    const contextPosition = this._elementPositions.get(element)?.contextPosition || 0;
    if (positionFromAttribute) {
      return channelName ? `${channelName}_${positionFromAttribute}` : positionFromAttribute;
    }
    if (isElementPositionIgnored) {
      return;
    }
    return `${channelName || "n/a"}_${contextPosition}`;
  }
  _getPageElementCreative(element) {
    const creativeFromAttribute = element.getAttribute("data-element-tracking-creative");
    const isElementPositionIgnored = this._isElementPositionIgnored(element);
    const stageArticleSourceType = element.getAttribute("data-element-tracking-stage-article-source-type");
    const { isStandalone, listItemsCount, listItemPosition } = this._elementPositions.get(element) || {};
    if (creativeFromAttribute) {
      return creativeFromAttribute;
    }
    if (isStandalone || isElementPositionIgnored) {
      return;
    }
    return `${listItemPosition}-${listItemsCount}${stageArticleSourceType ? ` | ${stageArticleSourceType}` : ""}`;
  }
  _getPageElementCampaignName(trackedEventInfo, clickableElementData) {
    const { element } = trackedEventInfo;
    const campaignNameFromAttr = element.getAttribute("data-element-tracking-campaign-name");
    if (campaignNameFromAttr) {
      return campaignNameFromAttr;
    }
    if (clickableElementData?.customLabel) {
      return clickableElementData?.customLabel;
    }
    if (clickableElementData) {
      const { ctfUuid, label } = clickableElementData;
      return `${ctfUuid ? "ctf-" + ctfUuid : "n/a"} | ${label}`;
    }
  }
  _createGetAttributeOrUndefined(element) {
    return (attr) => {
      return element.getAttribute(attr) || void 0;
    };
  }
  _isElementPositionIgnored(element) {
    return !element.getAttribute("data-element-tracking-context");
  }
  _isElementPositionPinned(element) {
    return element.hasAttribute("data-element-tracking-pin-position") && element.getAttribute("data-element-tracking-pin-position") === "true";
  }
  _groupElementsByContext(elements) {
    const groupedByContext = /* @__PURE__ */ new Map();
    elements.forEach((element) => {
      const context = element.getAttribute("data-element-tracking-context");
      if (!context) {
        return;
      }
      let elements2 = [];
      if (groupedByContext.has(context)) {
        elements2 = groupedByContext.get(context) || [];
      } else {
        groupedByContext.set(context, elements2);
      }
      const nestedContext = element.getAttribute("data-element-tracking-nested-context");
      if (nestedContext) {
        let nestedGroup = elements2.find(
          (group) => Array.isArray(group) && group[0].getAttribute("data-element-tracking-nested-context") === nestedContext
        );
        if (!nestedGroup) {
          nestedGroup = [];
          elements2.push(nestedGroup);
        }
        nestedGroup.push(element);
      } else {
        elements2.push(element);
      }
    });
    return Array.from(groupedByContext.values());
  }
  /**
   * Removes empty properties from the given event object.
   *
   * This method filters out properties with undefined, null, or empty string values
   * from the provided event object and its nested pageElementData array.
   *
   * @param event - The event object to be sanitized.
   * @returns A new event object with empty properties removed.
   */
  _removeEmptyEventProperties(event) {
    const removeEmpty = (obj) => {
      return Object.fromEntries(
        Object.entries(obj).filter(([, value]) => value !== void 0 && value !== null && value !== "")
      );
    };
    const sanitizedEvent = {
      // `Partial<IElementTrackingEvent>`:
      // optional properties with empty values are stripped
      ...removeEmpty(event),
      // Restore required fields to satisfy `IElementTrackingEvent`.
      // This also prevents a potential runtime issue: if an
      // event somehow had an empty required field (e.g.
      // `event_name`), `removeEmpty` would delete it, and
      // `utag.link` could crash operating with the missing
      // property.
      event_name: event.event_name,
      event_action: event.event_action,
      event_category: event.event_category,
      event_label: event.event_label
    };
    if (sanitizedEvent.pageElementData) {
      sanitizedEvent.pageElementData = sanitizedEvent.pageElementData.map(removeEmpty);
    }
    return sanitizedEvent;
  }
  /**
   * Dispatches the given element tracking event using the `utag.link` function.
   *
   * This method first removes empty properties from the event object.
   * If the `utag.link` function is available,it dispatches the sanitized event and logs the event to the console.
   * If `utag.link` is not available, it logs a warning to the console.
   *
   * @param event - The event object to be dispatched.
   */
  _dispatchElementTrackingEvent(event) {
    if (typeof window.utag !== "undefined" && typeof window.utag.link === "function") {
      const sanitizedEvent = this._removeEmptyEventProperties(event);
      window.utag.link(sanitizedEvent);
    } else {
      console.warn("utag.link is not available, event not sent:", event);
    }
  }
};
var DummyElementTrackingAPI = class {
  async bootstrapTracking() {
  }
};
var elementTrackingAPI = ElementTrackingAPI.getInstance();

// src/client/utils/textMilestoneTrackingAPI.ts
var ACTIVATION_META_SELECTOR = 'meta[name="textMilestoneTracking"][content="enabled"]';
var TEXT_BLOCK_SELECTOR = "[data-text-milestone-block]";
var SENTINEL_DATA_ATTR = "data-text-milestone";
var TAG_REGEX = /<p>&nbsp;<\/p>|<[^>]*>/g;
var NUMERIC_MILESTONES = [1500, 2500, 3500, 4500];
var NUMERIC_MILESTONE_TO_KEY = {
  1500: "1500",
  2500: "2500",
  3500: "3500",
  4500: "4500"
};
var TEXT_MILESTONE_KEYS_READ_ORDER = ["1500", "2500", "3500", "4500", "end"];
function parseSentinelMilestoneKey(raw) {
  if (raw === void 0 || raw === "") {
    return void 0;
  }
  for (const key of TEXT_MILESTONE_KEYS_READ_ORDER) {
    if (raw === key) {
      return key;
    }
  }
  return void 0;
}
var EVENT_VALUE_MAX_SECONDS = 1800;
var DummyTextMilestoneTrackingAPI = class {
  bootstrapTracking() {
  }
};
var TextMilestoneTrackingAPI = class _TextMilestoneTrackingAPI {
  static _instance = null;
  _initialized = false;
  // Baseline for `event_value`: matches the existing `pageElement.click`
  // origin in elementTrackingAPI (option A in the design discussion).
  _pageLoadTimer = 0;
  _observer = null;
  constructor() {
  }
  static getInstance() {
    if (typeof window === "undefined" || typeof document === "undefined") {
      return new DummyTextMilestoneTrackingAPI();
    }
    if (!_TextMilestoneTrackingAPI._instance) {
      _TextMilestoneTrackingAPI._instance = new _TextMilestoneTrackingAPI();
    }
    return _TextMilestoneTrackingAPI._instance;
  }
  bootstrapTracking() {
    if (this._initialized) {
      console.warn("TextMilestoneTrackingAPI: Already initialized.");
      return;
    }
    if (typeof IntersectionObserver === "undefined") {
      return;
    }
    if (!this._isActivationMarkerPresent()) {
      return;
    }
    if (this._getTextBlocks().length === 0) {
      return;
    }
    retryCallbackWithCondition(() => this._initializeTracking(), isUtagLinkDefinedOnWindow, 50, 100);
  }
  _initializeTracking() {
    this._initialized = true;
    this._pageLoadTimer = performance.now();
    const blockMeta = this._computeBlockMeta();
    const total = blockMeta.length > 0 ? blockMeta[blockMeta.length - 1].end : 0;
    const sentinels = this._insertSentinels(blockMeta, total);
    if (sentinels.length === 0) {
      return;
    }
    this._observer = new IntersectionObserver(
      (entries, observer) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) {
            return;
          }
          if (!(entry.target instanceof HTMLElement)) {
            return;
          }
          const key = parseSentinelMilestoneKey(entry.target.getAttribute(SENTINEL_DATA_ATTR) ?? void 0);
          if (!key) {
            observer.unobserve(entry.target);
            return;
          }
          this._dispatchMilestone(key);
          observer.unobserve(entry.target);
        });
      },
      { threshold: 0, rootMargin: "0px" }
    );
    sentinels.forEach((sentinel) => this._observer?.observe(sentinel));
  }
  _isActivationMarkerPresent() {
    return document.querySelector(ACTIVATION_META_SELECTOR) !== null;
  }
  _getTextBlocks() {
    return Array.from(document.querySelectorAll(TEXT_BLOCK_SELECTOR));
  }
  /**
   * Compute per-block cumulative char ranges using the SAME logic the server
   * uses for `pageTextFieldCharCount` (see Tracking.adapter.ts -> getPageTextFieldCharCount).
   */
  _computeBlockMeta() {
    const blocks = this._getTextBlocks();
    let cumulative = 0;
    return blocks.map((el) => {
      const charsInBlock = el.innerHTML.replace(TAG_REGEX, "").length;
      const meta = { el, start: cumulative, end: cumulative + charsInBlock };
      cumulative = meta.end;
      return meta;
    });
  }
  /**
   * Insert zero-width sentinel spans in the DOM at each applicable milestone
   * position and at end-of-article.
   *
   * Numeric milestones below `total` are skipped per spec; the `end` sentinel
   * is always created when there is at least one text block.
   *
   * Sentinel placement within a block is best-effort: char ranges per block
   * are server-parity, but locating the exact text-node offset uses
   * `textContent.length` which can drift slightly from the regex-stripped
   * count (mainly around `<p>&nbsp;</p>` paragraphs which the server filters
   * out entirely).
   */
  _insertSentinels(blockMeta, total) {
    const sentinels = [];
    NUMERIC_MILESTONES.forEach((milestone) => {
      if (total < milestone) {
        return;
      }
      const block = blockMeta.find((b) => milestone > b.start && milestone <= b.end);
      if (!block) {
        return;
      }
      const offsetWithinBlock = milestone - block.start;
      const sentinel = this._insertSentinelAtOffset(block.el, offsetWithinBlock, NUMERIC_MILESTONE_TO_KEY[milestone]);
      if (sentinel) {
        sentinels.push(sentinel);
      }
    });
    if (blockMeta.length > 0) {
      const lastBlock = blockMeta[blockMeta.length - 1];
      const endSentinel = this._appendSentinel(lastBlock.el, "end");
      if (endSentinel) {
        sentinels.push(endSentinel);
      }
    }
    return sentinels;
  }
  _insertSentinelAtOffset(blockEl, offset, key) {
    const walker = document.createTreeWalker(blockEl, NodeFilter.SHOW_TEXT, {
      acceptNode: (node) => {
        const parent = node.parentElement;
        if (parent && parent.tagName === "P" && parent.childNodes.length === 1 && (node.nodeValue === "\xA0" || node.nodeValue === "\xA0")) {
          return NodeFilter.FILTER_REJECT;
        }
        return NodeFilter.FILTER_ACCEPT;
      }
    });
    let consumed = 0;
    let targetNode = null;
    let targetOffset = 0;
    let current = walker.nextNode();
    while (current) {
      if (!(current instanceof Text)) {
        current = walker.nextNode();
        continue;
      }
      const len = current.nodeValue?.length ?? 0;
      if (consumed + len >= offset) {
        targetNode = current;
        targetOffset = Math.max(0, Math.min(len, offset - consumed));
        break;
      }
      consumed += len;
      current = walker.nextNode();
    }
    const sentinel = this._createSentinel(key);
    if (!targetNode) {
      blockEl.appendChild(sentinel);
      return sentinel;
    }
    const range = document.createRange();
    try {
      range.setStart(targetNode, targetOffset);
    } catch {
      blockEl.appendChild(sentinel);
      return sentinel;
    }
    range.collapse(true);
    range.insertNode(sentinel);
    return sentinel;
  }
  _appendSentinel(blockEl, key) {
    const sentinel = this._createSentinel(key);
    blockEl.appendChild(sentinel);
    return sentinel;
  }
  _createSentinel(key) {
    const sentinel = document.createElement("span");
    sentinel.setAttribute(SENTINEL_DATA_ATTR, key);
    sentinel.setAttribute("aria-hidden", "true");
    return sentinel;
  }
  /**
   * Compute `event_value` (elapsed seconds since page load baseline):
   * - round half-up (e.g. 12.5 -> 13)
   * - clamp to [0, 1800]
   * - return as string
   */
  _computeEventValue() {
    const elapsedSeconds = (performance.now() - this._pageLoadTimer) / 1e3;
    const rounded = Math.floor(elapsedSeconds + 0.5);
    const clamped = Math.min(EVENT_VALUE_MAX_SECONDS, Math.max(0, rounded));
    return String(clamped);
  }
  _dispatchMilestone(key) {
    const event = {
      event_name: `textMilestone.${key}`,
      event_action: `textMilestone.${key}`,
      event_category: "textMilestone",
      event_value: this._computeEventValue()
    };
    if (typeof window.utag !== "undefined" && typeof window.utag.link === "function") {
      window.utag.link(event);
    } else {
      console.warn("utag.link is not available, text milestone event not sent:", event);
    }
  }
  /**
   * Resets mutable singleton state for unit tests. Does not replace the singleton instance.
   * @internal
   */
  static resetForTests() {
    const inst = _TextMilestoneTrackingAPI._instance;
    if (inst instanceof _TextMilestoneTrackingAPI) {
      inst._initialized = false;
      inst._pageLoadTimer = 0;
      inst._observer = null;
    }
  }
};
function resetTextMilestoneTrackingForTests() {
  TextMilestoneTrackingAPI.resetForTests();
}
var textMilestoneTrackingAPI = TextMilestoneTrackingAPI.getInstance();

// src/client/login/login-storage.ts
var STORAGE_KEYS = {
  loginCallback: "auth0AfterLogin"
};

// src/client/login/login-analytics.ts
function dispatchLoginEvent(event, onDelivered) {
  if (typeof window.utagLinkCstFbck === "function") {
    window.utagLinkCstFbck(event);
    onDelivered?.();
    return;
  }
  retryCallbackWithCondition(() => {
    window.utag.link(event);
    onDelivered?.();
  }, isUtagLinkDefinedOnWindow);
}
function trackLoginComplete(userId) {
  if (localStorage.getItem(STORAGE_KEYS.loginCallback) === null) return;
  const loginType = userId.match(/(.*)\|/)?.[1] ?? "";
  dispatchLoginEvent(
    {
      event_name: "userLogin.complete",
      event_category: "userLogin",
      event_action: "userLogin.complete",
      event_label: "n/a",
      userType: "standard",
      userLoginType: loginType
    },
    () => localStorage.removeItem(STORAGE_KEYS.loginCallback)
  );
}
function trackLoginStatus() {
  dispatchLoginEvent({
    event_name: "userLoginStatus.loggedin",
    event_category: "userLoginStatus",
    event_action: "userLoginStatus.loggedin",
    event_label: "n/a"
  });
}
function trackLoginClick() {
  dispatchLoginEvent({
    event_name: "userLogin.click",
    event_category: "userLogin",
    event_action: "userLogin.click",
    event_label: "n/a"
  });
}

// src/client/login/login-api.ts
var AUTH0_CACHE_PREFIX = "@@auth0spajs@@";
var ServiceTokenError = class extends Error {
  reason;
  constructor(reason, cause) {
    super(`Service token exchange failed: ${reason}`, { cause });
    this.reason = reason;
    this.name = "ServiceTokenError";
  }
};
function auth0ErrorCode(e) {
  if (typeof e !== "object" || e === null || !("error" in e)) return void 0;
  return typeof e.error === "string" ? e.error : void 0;
}
var ExchangeCache = class {
  set(key, entry) {
    if (key.includes("@@user@@")) return;
    localStorage.setItem(key, JSON.stringify(entry));
  }
  get(key) {
    const json = localStorage.getItem(key);
    if (!json) return void 0;
    try {
      return JSON.parse(json);
    } catch {
      return void 0;
    }
  }
  remove(key) {
    localStorage.removeItem(key);
  }
  allKeys() {
    return Object.keys(localStorage).filter((key) => key.startsWith(AUTH0_CACHE_PREFIX));
  }
};
function createLoginService(clientPromise, config) {
  const inflightExchange = /* @__PURE__ */ new Map();
  async function doExchange(audience, scope) {
    const exchangeClient = await window.auth0.createAuth0Client({
      domain: config.auth0Domain,
      clientId: config.auth0ClientId,
      cache: new ExchangeCache()
    });
    try {
      const cached = await exchangeClient.getTokenSilently({
        cacheMode: "cache-only",
        authorizationParams: { audience, scope }
      });
      if (cached) return exchangeClient;
    } catch {
    }
    let brandToken;
    try {
      const client = await clientPromise;
      brandToken = await client.getTokenSilently({
        authorizationParams: { audience: config.oauthAudience }
      });
    } catch (e) {
      const code = auth0ErrorCode(e);
      const isUnauthenticated = code === "login_required" || code === "missing_refresh_token" || code === "invalid_grant";
      throw new ServiceTokenError(isUnauthenticated ? "unauthenticated" : "unknown", e);
    }
    try {
      await exchangeClient.loginWithCustomTokenExchange({
        subject_token: brandToken,
        subject_token_type: config.auth0TokenExchangeProfile,
        audience,
        scope
      });
    } catch (e) {
      const isUnauthorized = auth0ErrorCode(e) === "access_denied";
      throw new ServiceTokenError(isUnauthorized ? "unauthorized" : "unknown", e);
    }
    return exchangeClient;
  }
  return {
    getClient() {
      return clientPromise;
    },
    getConfig() {
      return config;
    },
    legacy: {
      login: async (options) => {
        const client = await clientPromise;
        const returnUrl = options?.returnUrl ?? window.location.href;
        trackLoginClick();
        client.loginWithRedirect({
          appState: { returnUrl },
          authorizationParams: { redirect_uri: window.location.origin }
        }).catch((error) => console.error("Login redirect failed", error));
      },
      logout: async (_options) => {
        const client = await clientPromise;
        await client.logout({ logoutParams: { returnTo: window.location.origin } });
      },
      async getCachedIdToken() {
        const client = await clientPromise;
        const claims = await client.getIdTokenClaims();
        return claims?.__raw;
      },
      async isAuthenticatedBasedOnCachedToken() {
        const client = await clientPromise;
        const isAuthenticated = await client.isAuthenticated();
        if (!isAuthenticated) {
          return false;
        }
        const claims = await client.getIdTokenClaims();
        if (!claims?.exp) {
          return false;
        }
        const earlyExpiryMs = 1.5 * 60 * 60 * 1e3;
        const effectiveExpiryMs = claims.exp * 1e3 - earlyExpiryMs;
        return effectiveExpiryMs > Date.now();
      }
    },
    getTokenExchangeClient(audience, scope) {
      const key = `${audience}::${scope}`;
      const existing = inflightExchange.get(key);
      if (existing) return existing;
      const promise = doExchange(audience, scope).finally(() => {
        inflightExchange.delete(key);
      });
      inflightExchange.set(key, promise);
      return promise;
    },
    trackLoginClick
  };
}

// src/client/login/login-client-init.ts
function createAuth0Client(config) {
  return window.auth0.createAuth0Client({
    domain: config.auth0Domain,
    clientId: config.auth0ClientId,
    cacheLocation: "localstorage",
    authorizationParams: {
      redirect_uri: window.location.origin,
      audience: config.oauthAudience
    },
    useRefreshTokens: true
  });
}
function handleLoginPage(client, isAuthenticated) {
  if (window.location.pathname !== "/login/") return false;
  if (isAuthenticated) {
    window.location.replace(window.location.origin);
  } else {
    client.loginWithRedirect({ authorizationParams: { redirect_uri: window.location.origin } }).catch((error) => console.error("Login redirect failed", error));
  }
  return true;
}
async function handleOAuthCallback(client) {
  const query = window.location.search;
  if (!query.includes("state=") || !query.includes("code=") && !query.includes("error=")) {
    return false;
  }
  const result = await client.handleRedirectCallback();
  localStorage.setItem(STORAGE_KEYS.loginCallback, window.location.href);
  const appState = result.appState;
  const returnUrl = appState && typeof appState === "object" && "returnUrl" in appState && typeof appState.returnUrl === "string" ? appState.returnUrl : window.location.origin;
  window.location.replace(returnUrl);
  return true;
}
async function handleAuthenticatedUser(client) {
  const user = await client.getUser();
  if (user?.sub) {
    trackLoginComplete(user.sub);
  }
  trackLoginStatus();
}

// src/client/login/load-login.ts
async function loadLoginSystem(config) {
  let resolveClient;
  const clientPromise = new Promise((resolve) => {
    resolveClient = resolve;
  });
  window.__auth0API = createLoginService(clientPromise, config);
  await new Promise((resolve, reject) => {
    const script = document.createElement("script");
    script.src = config.auth0ScriptUrl;
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error(`Failed to load script from ${config.auth0ScriptUrl}`));
    document.body.appendChild(script);
  });
  const client = await createAuth0Client(config);
  const isAuthenticated = await client.isAuthenticated();
  if (handleLoginPage(client, isAuthenticated)) return;
  if (await handleOAuthCallback(client)) return;
  if (isAuthenticated) {
    await handleAuthenticatedUser(client);
  }
  resolveClient(client);
}

// src/utils/imageLazyLoader.constants.ts
var IMAGE_LAZY_LOADER_MARKER_CLASS = "Lazy-Image";
var IMAGE_LAZY_LOADER_LOADING_CLASS = "Lazy-Image--Loading";
var IMAGE_LAZY_LOADER_ERROR_CLASS = "Lazy-Image--Error";

// src/client/utils/imageLazyLoader.util.ts
var LOADER_SELECTOR = `img.${IMAGE_LAZY_LOADER_MARKER_CLASS}`;
var trackedImages = /* @__PURE__ */ new WeakSet();
var insertedImagesObserver;
var trackImage = (img) => {
  if (trackedImages.has(img)) {
    return;
  }
  trackedImages.add(img);
  const resolveState = (hasFailed) => {
    img.classList.remove(IMAGE_LAZY_LOADER_LOADING_CLASS);
    if (hasFailed) {
      img.classList.add(IMAGE_LAZY_LOADER_ERROR_CLASS);
    }
  };
  if (img.complete) {
    resolveState(img.naturalWidth === 0);
    return;
  }
  const controller = new AbortController();
  const onSettled = (hasFailed) => {
    controller.abort();
    resolveState(hasFailed);
  };
  img.addEventListener("load", () => onSettled(false), { signal: controller.signal });
  img.addEventListener("error", () => onSettled(true), { signal: controller.signal });
};
var trackImagesIn = (root) => {
  if (root instanceof HTMLImageElement && root.matches(LOADER_SELECTOR)) {
    trackImage(root);
  }
  if (root instanceof Element || root instanceof Document) {
    root.querySelectorAll(LOADER_SELECTOR).forEach(trackImage);
  }
};
var registerImageLazyLoader = () => {
  trackImagesIn(document);
  insertedImagesObserver?.disconnect();
  insertedImagesObserver = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      mutation.addedNodes.forEach((node) => {
        if (node instanceof Element) {
          trackImagesIn(node);
        }
      });
    });
  });
  insertedImagesObserver.observe(document.body, { childList: true, subtree: true });
};

// src/client/utils/page.client.util.ts
var initClientPage = () => {
  if (window.__auth0APIReady !== void 0) {
    const authReady = window.__auth0APIReady;
    loadLoginSystem(authReady._config).then(() => authReady._resolve(true)).catch(() => authReady._resolve(false));
  }
  waitForDocumentReady().then(() => {
    registerConsentLayerListener();
    registerImageLazyLoader();
    elementTrackingAPI.bootstrapTracking();
    textMilestoneTrackingAPI.bootstrapTracking();
  }).catch(() => {
  });
};
var decompressIslands = (pageContext) => {
  pageContext.islands = parse(pageContext.islandsCompressed);
  pageContext.islandsCompressed = "";
};

// src/client/utils/conversarioAPI.ts
var Conversario = class _Conversario {
  events = {
    commentsOpened: "conversarioCommentsOpened",
    commentsClosed: "conversarioCommentsClosed",
    commentsLoading: "conversarioLoading",
    commentsLoaded: "conversarioLoaded"
  };
  static _instance;
  _openTimer = 0;
  _isOpen = false;
  _commentsUrlHash = "#comments";
  constructor() {
  }
  static getInstance() {
    if (!_Conversario._instance) {
      _Conversario._instance = new _Conversario();
    }
    return _Conversario._instance;
  }
  openComments = (options) => {
    const urlParams = new URLSearchParams(window.location.search);
    const isDebateSource = urlParams.get("debatebutton") === "true";
    const { className = "", isOpenOnPageLoad } = options ?? {};
    const isApp = !!options?.isApp || checkIsAppView();
    const hasCommentsHash = window.location.hash.includes(this._commentsUrlHash);
    const shouldNavigateToAppCommentsUrl = isApp && !hasCommentsHash;
    if (shouldNavigateToAppCommentsUrl) {
      const url = new URL(window.location.href);
      url.searchParams.set("comment", "true");
      window.location.replace(`${url.toString()}${this._commentsUrlHash}`);
      return;
    }
    if (!hasCommentsHash) {
      window.location.hash = "comments";
    }
    this._dispatchCustomEvent(this.events.commentsOpened);
    this._isOpen = true;
    if (isOpenOnPageLoad) {
      let openSource = "app";
      let openPosition = "n/a";
      if (!isApp) {
        openSource = "link";
        openPosition = this._getTrackPosition();
      } else if (isDebateSource) {
        openSource = "article-link-debate-button";
        openPosition = "article";
      }
      this._trackCommentEvents("comment_opened", openSource, openPosition);
    } else {
      this._trackCommentEvents("comment_opened", className, "article");
    }
    this._timeSpent();
    window.addEventListener("beforeunload", this._trackCommentEventsOnPageUnload);
  };
  closeComments = (options) => {
    this._dispatchCustomEvent(this.events.commentsClosed);
    this._isOpen = false;
    const isApp = !!options?.isApp || checkIsAppView();
    const hasCommentsHash = window.location.hash.includes(this._commentsUrlHash);
    if (hasCommentsHash) {
      if (isApp) {
        const url = new URL(window.location.href);
        url.hash = "";
        url.searchParams.delete("comment");
        history.replaceState(null, "", url.toString());
      } else {
        history.replaceState(null, "", " ");
      }
    }
    const { className = "", isClosedOnPageUnload } = options ?? {};
    if (isClosedOnPageUnload) {
      this._trackCommentEvents("comment_closed", "window_closed", "", this._timeSpent(true));
    } else {
      this._trackCommentEvents("comment_closed", className, "", this._timeSpent(true));
    }
    window.removeEventListener("beforeunload", this._trackCommentEventsOnPageUnload);
  };
  logIntoComments = () => {
    window.__auth0API.legacy.login({ returnUrl: window.location.href.split("#")[0] + this._commentsUrlHash });
  };
  logoutOfComments = () => {
    window.__auth0API.legacy.logout({ returnUrl: window.location.href.split("#")[0] });
  };
  async loadEngagentlyLibraryAndComments(options) {
    await window.__auth0API.legacy.isAuthenticatedBasedOnCachedToken();
    const token = await window.__auth0API.legacy.getCachedIdToken();
    const { clientLibraryUrl, communityId, isApp } = options;
    this._initCommentsLibEngagently({ communityId, token, isApp });
    const engagentlyScript = document.createElement("script");
    engagentlyScript.src = clientLibraryUrl;
    engagentlyScript.setAttribute("data-engagently", "");
    engagentlyScript.setAttribute("type", "module");
    engagentlyScript.async = true;
    document.body.appendChild(engagentlyScript);
  }
  _initCommentsLibEngagently(options) {
    const { communityId, token, isApp } = options;
    const initEngagently = () => {
      if (!communityId) {
        return;
      }
      const htmlTheme = document.documentElement.getAttribute("data-theme");
      let colorScheme;
      if (htmlTheme === "dark") {
        colorScheme = "dark";
      } else if (htmlTheme === "light") {
        colorScheme = "light";
      } else {
        colorScheme = "auto";
      }
      window.engagently.Configuration({
        communityId,
        token,
        colorScheme: isApp ? colorScheme : "light",
        defaultLanguage: "de",
        events: {
          onLoadMoreComments: () => {
            this._trackCommentEvents("comment_loadedMore");
          },
          onSubmitCommentary: () => {
            this._trackCommentEvents("comment_added");
          }
        },
        translations: {
          de: {
            messages: {
              "discussion-preamble": 'Herzlich willkommen im Kommentarteil von  FOCUS online. Bitte beachten Sie unsere <a target="_parent" href="https://www.focus.de/static/netiquette_db4e618c-329d-4e92-9183-6f438f76f712.html">Richtlinien</a>, die Kommentare werden von uns moderiert.'
            },
            common: {
              "shared-comment": "Ausgew\xE4hlter {{comment}}"
            }
          }
        }
      });
    };
    window.initEngagently = initEngagently;
  }
  _dispatchCustomEvent(eventName) {
    const event = new CustomEvent(eventName, { bubbles: true, cancelable: true });
    document.dispatchEvent(event);
  }
  _trackCommentEvents(name, creative, position, value) {
    const callback = () => {
      window.utag.link({
        event_name: "pageElement.click",
        event_category: "pageElement",
        event_action: "pageElement.clickInternal",
        event_label: "n/a",
        event_value: value || "",
        pageElementType: "conversario",
        pageElementName: name,
        pageElementPosition: position || "n/a",
        pageElementCreative: creative || "n/a"
      });
    };
    retryCallbackWithCondition(callback, isUtagLinkDefinedOnWindow);
  }
  _getTrackPosition() {
    if (!document.referrer) {
      return "new_window";
    }
    if (/\/$/.test(document.referrer)) {
      return "homepage";
    }
    if (/\.html/.test(document.referrer)) {
      return "article";
    }
    return "section";
  }
  _timeSpent(isClosing = false) {
    if (isClosing) {
      const _timeSpent = Math.round((Date.now() - this._openTimer) / 1e3);
      this._openTimer = 0;
      return _timeSpent.toString();
    } else {
      this._openTimer = Date.now();
    }
  }
  _trackCommentEventsOnPageUnload = () => {
    if (this._isOpen) {
      this._trackCommentEvents("comment_closed", "window_closed", "", this._timeSpent(true));
    }
  };
};
var conversario = Conversario.getInstance();
var generateTableOfContentsId = (text) => {
  return cleanCssIdentifier(stripHtmlTags(text) ?? "");
};

// src/client/utils/version.util.ts
var parseVersion = (version) => {
  const match = /^(\d+)\.(\d+)\.(\d+)/.exec(version);
  if (!match) return void 0;
  return [Number(match[1]), Number(match[2]), Number(match[3])];
};
var isVersionAtLeast = (version, minimum) => {
  const parsedVersion = parseVersion(version);
  const parsedMinimum = parseVersion(minimum);
  if (!parsedVersion || !parsedMinimum) return false;
  const [majorVersion, minorVersion, patchVersion] = parsedVersion;
  const [majorMinimum, minorMinimum, patchMinimum] = parsedMinimum;
  if (majorVersion !== majorMinimum) return majorVersion > majorMinimum;
  if (minorVersion !== minorMinimum) return minorVersion > minorMinimum;
  return patchVersion >= patchMinimum;
};
var supportsImageFullscreenFromAppVersion = (appVersion, minAppVersion) => {
  if (typeof appVersion !== "string" || appVersion.length === 0) return false;
  return isVersionAtLeast(appVersion, minAppVersion);
};

export { ChannelElementTrackingAdapter, ElementTrackingAdapter, Vendors, attachConsentLayerListener, buildArticleTeaserTrackingCampaignName, checkAndSetConsent, checkIsAdFree, checkIsAppView, checkIsGoogleBot, conversario, decompressIslands, elementTrackingAPI, generateTableOfContentsId, getConsentManagerModalOpener, getConsentRevealClickHandler, getUtiqManageModalOpener, getVendorConsent, grantConsentForVendor, grantConsentForVendorById, hasLegacyPurposeBasedWidgetConsent, initClientPage, isCpxLink, isInternalLink, isUtagDefinedOnWindow, isUtagLinkDefinedOnWindow, isVersionAtLeast, layoutBreakpointCategory, layoutDeviceCategory, loadWidgetScript, parseVersion, registerConsentLayerListener, resetTextMilestoneTrackingForTests, retryCallbackWithCondition, sanitizeString, supportsImageFullscreenFromAppVersion, textMilestoneTrackingAPI, usePurposeBasedWidgetConsentState, useWidgetConsentState, waitForDocumentReady };
//# sourceMappingURL=client.js.map
//# sourceMappingURL=client.js.map